import type { LocaleDictionary } from "@/i18n/types";

const nsVersionJA = {
  fallbackName: "バージョン",
  unknownTitle: "不明なバージョン",
  list: {
    showLatestOnly: ({ count }: { count: number }) => `最新${count}件のみ表示`,
    showAll: "すべて表示する",
    pageTitle: "バージョン一覧",
    column: {
      name: "バージョン名",
      status: "ステータス",
      download: "ダウンロード",
      category: "カテゴリー",
      publishedAt: "発行時刻",
      updatedAt: "最終更新時刻",
      viewers: "閲覧者",
    },
    filterByName: "バージョン名で絞り込む",
    editTitleAriaLabel: "タイトルを編集",
    resetFilter: "条件をリセット",
    tabEnabled: "有効なバージョン",
    tabDisabled: "削除されたバージョン",
    disabledTabNote: "30日経過したバージョンは自動的に完全削除されます。",
  },
  notFoundError: "バージョンが見つかりません",
  removeDialog: {
    title: "バージョンの完全削除",
    message: ({ versionName }: { versionName: string }) =>
      `${versionName}を完全に削除します。`,
    warning: "削除したバージョンは元に戻せません。",
    button: "完全削除",
    inProgressButton: "完全削除中...",
    success: "バージョンを完全に削除しました",
    error: "バージョンを完全に削除できませんでした",
  },
  removeSomeDialog: {
    title: "選択バージョンの完全削除",
    message: "選択したバージョンを完全削除しますか？",
    warning: "削除したバージョンは元に戻せません。",
    startButton: "完全削除開始",
  },
  rename: {
    success: "バージョン名を変更しました",
    error: "バージョン名を変更できませんでした",
  },
  operation: {
    optium: {
      create: "新規作成",
      upload: "ファイルアップロード",
      evaluate: "評価",
      optimize: "最適化",
      file_optimize: "ファイル最適化",
      file_evaluate: "ファイル評価",
    },
    planium: {
      create: "新規作成",
      upload: "ファイルアップロード",
      evaluate: "評価",
      optimize: "自動ドラフト",
      file_optimize: "ファイル最適化",
      file_evaluate: "ファイル評価",
    },
  },
  status: {
    initial: "初期状態",
    optium: {
      queued: "処理待機中",
      queued_optimization: "最適化待機中",
      in_progress_evaluation: "評価実行中",
      in_progress_optimization: "最適化実行中",
      success_evaluation: "評価成功",
      success_optimization: "最適化成功",
      failure_in_optimization: "最適化失敗",
      failure_input_evaluation: "入力検証失敗",
      failure_exception: "エラー",
      canceled_evaluation: "評価キャンセル",
      canceled_optimization: "最適化キャンセル",
    },
    planium: {
      queued: "処理待機中",
      queued_optimization: "自動ドラフト待機中",
      in_progress_evaluation: "評価実行中",
      in_progress_optimization: "自動ドラフト実行中",
      success_evaluation: "評価成功",
      success_optimization: "自動ドラフト成功",
      failure_in_optimization: "自動ドラフト失敗",
      failure_input_evaluation: "入力検証失敗",
      failure_exception: "エラー",
      canceled_evaluation: "評価キャンセル",
      canceled_optimization: "自動ドラフトキャンセル",
    },
  },
  download: {
    inputButton: "入力",
    resultButton: "結果",
    errorButton: "エラー",
    inputSuccess: "入力ダウンロードに成功しました",
    inputError: "入力ダウンロードに失敗しました。",
    resultSuccess: "結果ダウンロードに成功しました",
    resultError: "結果ダウンロードに失敗しました",
    resultNotFoundError: "ファイルが見つかりませんでした",
    resultServerError: "サーバーエラーが発生しました",
    errorDetailSuccess: "エラー内容のダウンロードに成功しました",
    errorDetailError: "エラー内容のダウンロードに失敗しました。",
    multiMenuItem: "入力・結果ダウンロード",
    multiInputs: ({ count, max }: { count: number; max: number }) =>
      `${count}個の入力をダウンロード (最大${max}個まで)`,
    multiResults: ({ count, max }: { count: number; max: number }) =>
      `${count}個の結果をダウンロード (最大${max}個まで)`,
    multiInputsSuccess: "選択バージョンの入力ダウンロードに成功しました",
    multiInputsError: "選択バージョンの入力ダウンロードに失敗しました。",
    multiResultsSuccess: "選択バージョンの結果ダウンロードに成功しました",
    multiResultsError: "選択バージョンの結果ダウンロードに失敗しました。",
  },
  toolbar: {
    duplicate: "複製",
    delete: "削除",
    moreActions: "その他アクション",
    restore: "復元",
    remove: "完全削除",
    updateViewSetting: "ビュー設定更新",
    updateSheetSetting: "シート設定更新",
    selectTargetHint: "対象バージョンを選択してください",
  },
  create: {
    newVersion: "新規バージョンを作成",
    newVersionFromConfigFile: "設定ファイルから新規バージョンを作成",
  },
  category: {
    uncategorized: "未分類",
    filterPlaceholder: "カテゴリーで絞り込む",
    create: "新規カテゴリーを作成",
    rename: "カテゴリー名を変更",
    sort: "カテゴリーの並び替え",
    delete: "カテゴリーを削除",
    notFoundError: "カテゴリーが見つかりませんでした",
    deleteDialog: {
      title: "カテゴリーを削除",
      message: ({ categoryName }: { categoryName: string }) =>
        `${categoryName} を削除してもよろしいですか？バージョンは未分類に移動されます。`,
      messageWithoutName:
        "このカテゴリーを削除してもよろしいですか？バージョンは未分類に移動されます。",
      button: "削除",
      inProgressButton: "削除中...",
    },
    deleteSuccess: "カテゴリーを削除しました",
    deleteError: "カテゴリーを削除できませんでした",
    deleteSortError:
      "カテゴリーを削除しましたが、一部の並び順更新に失敗しました",
  },
  priority: {
    label: "優先バージョン",
    setMenuItem: "優先バージョンに指定",
    unsetMenuItem: "優先バージョン指定を解除",
    setDialog: {
      title: "優先バージョンに指定",
      message: ({
        categoryName,
        versionTitle,
      }: {
        categoryName: string;
        versionTitle: string;
      }) =>
        `「${categoryName}」で「${versionTitle}」を優先バージョンに指定しますか？`,
      currentPriorityNote: ({ versionTitle }: { versionTitle: string }) =>
        `現在の優先バージョン「${versionTitle}」の指定は解除されます。`,
      button: "指定",
    },
    unsetDialog: {
      title: "優先バージョン指定の解除",
      message: ({
        categoryName,
        versionTitle,
      }: {
        categoryName: string;
        versionTitle: string;
      }) =>
        `${categoryName}の「${versionTitle}」の優先バージョン指定を解除しますか？`,
      button: "解除",
    },
    updateSuccess: "優先バージョンを更新しました",
    updateError: "優先バージョンの更新に失敗しました",
    changedByOtherUserError: ({ versionTitle }: { versionTitle: string }) =>
      `優先バージョンが他のユーザーによって「${versionTitle}」に変更されています。ページを再読み込みしてください。`,
    clearedByOtherUserError:
      "優先バージョンが他のユーザーによって解除されています。ページを再読み込みしてください。",
  },
  actionsMenu: {
    ariaLabel: "バージョンメニュー",
  },
  row: {
    openViewAndSheetList: "ビュー・シート一覧を開く",
    versionInfo: "バージョン情報を確認・編集",
    rename: "バージョン名を変更",
    copyName: "バージョン名をコピー",
    duplicate: "バージョンを複製",
    delete: "バージョンを削除",
  },
  copyName: {
    success: "バージョン名をコピーしました",
    error: "バージョン名のコピーに失敗しました",
  },
  bulkUpdate: {
    targetName: {
      sheetSchema: "シート設定",
      viewSetting: "ビュー設定",
    },
    dialogTitle: ({ targetName }: { targetName: string }) =>
      `${targetName}更新`,
    button: "一括更新",
    description: ({
      count,
      targetName,
    }: {
      count: number;
      targetName: string;
    }) =>
      `選択された ${count} 件のバージョンに対して${targetName}を一括更新します。`,
    selectFile: "更新用のYAMLファイルを選択してください。",
    optionsLabel: "更新オプション",
    statusListTitle: "更新ステータス",
    allowDestructive: {
      sheetSchema:
        "シートおよびシートスキーマの削除・名前変更を含む変更を適用する",
      viewSetting: "ビューの削除を含む変更を適用する",
    },
    replaceConfirm: {
      title: {
        sheetSchema: "削除・名前変更を伴う更新の確認",
        viewSetting: "削除を伴う更新の確認",
      },
      message: {
        sheetSchema:
          "YAMLとの差分により、既存のシートやシートスキーマに対して削除や名前変更などの破壊的な更新が行われる可能性があります。",
        viewSetting: "YAMLに含まれないビューは削除されます。",
      },
      button: {
        sheetSchema: "削除・名前変更を許可して続行",
        viewSetting: "削除を許可して続行",
      },
    },
  },
  updateCategory: {
    clearPriorityError: "優先バージョンのクリアに失敗しました",
    clearPriorityRollbackError: ({
      clearError,
      rollbackError,
    }: {
      clearError: string;
      rollbackError: string;
    }) =>
      `優先バージョンのクリアに失敗し、ロールバックも失敗しました。データが不整合な状態になっている可能性があります。\n優先バージョンクリアエラー: ${clearError}\nロールバックエラー: ${rollbackError}`,
    success: ({ count }: { count: number }) =>
      count === 1
        ? "カテゴリーを変更しました"
        : `${count}件のカテゴリーを変更しました`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "カテゴリーを変更できませんでした"
        : `${count}件のカテゴリーを変更できませんでした`,
    menuItem: "カテゴリー変更",
    groupHeader: "新しいカテゴリーを選択",
  },
  referencedSheetsCheckError: "シート参照のチェックに失敗しました",
  duplicate: {
    inProgress: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを複製中..."
        : `${count}件のバージョンを複製中...`,
    success: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを複製しました"
        : `${count}件のバージョンを複製しました`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを複製できませんでした"
        : `${count}件のバージョンを複製できませんでした`,
    notCopyableError: ({ versionId }: { versionId: string }) =>
      `バージョンID ${versionId} は複製できません。`,
    dialog: {
      title: "バージョンの複製",
      message: ({ versionName }: { versionName: string }) =>
        `${versionName}を複製します。新しいバージョンの名前を入力してください。`,
      button: "複製",
    },
  },
  restore: {
    inProgress: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを復元中..."
        : `${count}件のバージョンを復元中...`,
    success: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを復元しました"
        : `${count}件のバージョンを復元しました`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを復元できませんでした"
        : `${count}件のバージョンを復元できませんでした`,
  },
  delete: {
    success: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを削除しました"
        : `${count}件のバージョンを削除しました`,
    error: ({ count }: { count: number }) =>
      count === 1
        ? "バージョンを削除できませんでした"
        : `${count}件のバージョンを削除できませんでした`,
  },
  deletedBand: {
    title: "削除済みのバージョンです",
    message: "削除から30日が経過すると、自動的に完全に削除されます。",
  },
  deleteDialog: {
    title: "バージョンの削除",
    message: ({ versionName }: { versionName: string }) =>
      `${versionName}は30日後に完全に削除されます。完全に削除されるまでは、削除されたバージョンの一覧から復元できます。`,
    messageSome: ({ count }: { count: number }) =>
      `${count}件のバージョンが30日後に完全に削除されます。完全に削除されるまでは、削除されたバージョンの一覧から復元できます。`,
    referencedSheetsWarning:
      "他のシートから参照されているシートが含まれるため、表示が破壊されることがあります。",
    button: "削除",
    inProgressButton: "削除中...",
    error: "バージョンの削除に失敗しました",
  },
  cancel: {
    target: {
      optium: "評価・最適化",
      planium: "自動ドラフト",
    },
    menuItem: ({ target }: { target: string }) => `${target}を中止`,
    dialog: {
      title: ({ target }: { target: string }) => `${target}の中止`,
      message: ({
        target,
        versionName,
      }: {
        target: string;
        versionName: string;
      }) => `バージョン「${versionName}」の${target}を中止しますか？`,
      messageWithoutName: ({ target }: { target: string }) =>
        `${target}を中止しますか？`,
      warning: "一度中止すると、再開できません。",
      button: "中止する",
      cancelButton: "続ける",
    },
    success: ({
      target,
      versionName,
    }: {
      target: string;
      versionName: string;
    }) => `バージョン「${versionName}」の${target}を中止しました。`,
    error: ({ target, versionName }: { target: string; versionName: string }) =>
      `バージョン「${versionName}」の${target}の中止に失敗しました。`,
  },
  fileOptimize: {
    menuButton: "ファイル最適化",
    dialog: {
      title: "ファイル最適化",
      description:
        "ファイルに対して評価 / 最適化を実行し、新しいバージョンを作成します。",
      button: "実行",
      evaluateOnly: "評価のみ実行する",
      versionTitle: "バージョンタイトル",
      versionComment: "バージョンコメント",
      category: "新しいバージョンに付与するカテゴリー",
      algorithmVersion: "使用するアルゴリズムバージョン",
      noAlgorithmVersion:
        "アルゴリズムバージョンが存在しません。作成してください",
      noAlgorithmVersionPermission:
        "アルゴリズムバージョンを閲覧する権限がありません。",
      algorithmVersionId: "アルゴリズムバージョンID(自動入力)",
    },
    startSuccess:
      "ファイル最適化を開始しました。完了までしばらくお待ちください",
    error: "ファイル最適化に失敗しました",
  },
  comparison: {
    pageTitle: "バージョン サマリ比較",
    description: {
      compare: "基準にするバージョンを選択して比較できます。",
      sortHint: "列見出しをクリックすると並び替えできます。",
      download: "各バージョンのサマリの一覧です。",
      downloadHint:
        "サマリをCSV形式でダウンロードできます。ダウンロードするバージョンを選択してください。",
    },
    filterLabel: "バージョン名、カテゴリー、サマリ内容でフィルタ",
    searchPlaceholder: "バージョン名、カテゴリー、サマリ内容で検索",
    baseLabel: ({ name }: { name: string }) => `${name}を比較の基準にする`,
    baseBadge: "基準",
    maxCount: "最大表示件数",
    latestCountOption: ({ count }: { count: number }) => `最新${count}件`,
    column: {
      versionTitle: "バージョン名",
      updatedAt: "更新日時",
      category: "カテゴリー",
      alertCount: "アラート件数",
      errorCount: "エラー件数",
    },
    unit: {
      count: "件",
    },
    download: {
      enterMode: "ダウンロードモードへ",
      submit: "CSVでダウンロード",
      selectionBarLabel: "選択したバージョンの操作",
      backToCompare: "比較モードへ戻る",
      clearSelection: ({ count }: { count: number }) => `${count}件を選択解除`,
      failure: "サマリのダウンロードに失敗しました",
      csv: {
        summaryName: ({ level }: { level: number }) => `サマリ名${level}`,
        unit: "単位",
      },
    },
    loadError: "データの取得に失敗しました",
  },
} satisfies LocaleDictionary;

export default nsVersionJA;
